import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import { Polyline, Marker, Tooltip, useMap, useMapEvents } from "react-leaflet";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const COLOR = "#14b8a6";
export const arrowIcon = (deg, color = COLOR) => L.divIcon({
  className: "",
  html: `<svg width="17" height="17" viewBox="0 0 14 14" style="transform:rotate(${deg}deg);filter:drop-shadow(0 0 2px #000)"><path d="M7 1 L12 12 L7 9 L2 12 Z" fill="${color}"/></svg>`,
  iconSize: [17, 17], iconAnchor: [8.5, 8.5],
});

const meters = (a, b) => {
  const dx = (b[1] - a[1]) * Math.cos((a[0] * Math.PI) / 180) * 111320;
  const dy = (b[0] - a[0]) * 110540;
  return [dx, dy, Math.hypot(dx, dy)];
};

export const arrows = (coords, step = 70) => {
  const out = [];
  let acc = step / 2;
  for (let i = 0; i < coords.length - 1; i++) {
    const [dx, dy, L2] = meters(coords[i], coords[i + 1]);
    let pos = acc;
    while (pos < L2) {
      const t = pos / L2;
      out.push({ p: [coords[i][0] + (coords[i + 1][0] - coords[i][0]) * t, coords[i][1] + (coords[i + 1][1] - coords[i][1]) * t], deg: (Math.atan2(dx, dy) * 180) / Math.PI });
      pos += step;
    }
    acc = pos - L2;
  }
  if (!out.length && coords.length > 1) {
    const [dx, dy] = meters(coords[0], coords[coords.length - 1]);
    out.push({ p: coords[Math.floor(coords.length / 2)], deg: (Math.atan2(dx, dy) * 180) / Math.PI });
  }
  return out;
};

export const OneWayLayer = ({ onStatus, onPick }) => {
  const map = useMap();
  const [ways, setWays] = useState([]);
  const loaded = useRef(new Set());
  const busy = useRef(false);

  const load = useCallback(async () => {
    if (map.getZoom() < 15) { onStatus?.("Acerque el mapa (zoom ≥ 15) para ver los sentidos"); return; }
    const b = map.getBounds();
    const key = `${b.getCenter().lat.toFixed(3)},${b.getCenter().lng.toFixed(3)}`;
    if (loaded.current.has(key) || busy.current) return;
    busy.current = true;
    onStatus?.("Cargando sentidos de circulación…");
    try {
      const r = await api.get("/oneways", { params: { minlon: b.getWest(), minlat: b.getSouth(), maxlon: b.getEast(), maxlat: b.getNorth() } });
      loaded.current.add(key);
      setWays((prev) => {
        const ids = new Set(prev.map((w) => w.id));
        return [...prev, ...r.data.ways.filter((w) => !ids.has(w.id))];
      });
      onStatus?.(null);
    } catch (e) {
      toast.error(errMsg(e));
      onStatus?.("No disponible");
    } finally { busy.current = false; }
  }, [map, onStatus]);

  useEffect(() => { load(); }, [load]);
  useMapEvents({ moveend: load });

  return (
    <>
      {ways.map((w) => (
        <Polyline key={`${w.id}-${onPick ? "p" : "v"}`} positions={w.coords} pathOptions={{ color: COLOR, weight: onPick ? 9 : 3, opacity: onPick ? 0.55 : 0.75 }} interactive
          eventHandlers={onPick ? { click: (e) => { L.DomEvent.stopPropagation(e); onPick(w); } } : undefined}>
          <Tooltip sticky><div className="text-xs">Sentido único{w.name ? ` · ${w.name}` : ""}</div>{onPick && <div className="text-[0.625rem]">Clic para invertir el sentido</div>}</Tooltip>
        </Polyline>
      ))}
      {ways.flatMap((w) => arrows(w.coords).map((a, i) => (
        <Marker key={`${w.id}-${i}`} position={a.p} icon={arrowIcon(a.deg)} interactive={false} />
      )))}
    </>
  );
};
